import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { PermitStep } from "@ha/data/basic";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Select, Store } from "@ngxs/store";
import { combineLatest, Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, take } from "rxjs/operators";
import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";
import { PermitPaths } from "../../../permit-paths";

import { PermitCreateActions } from "../../../permit-create/states/permit-create.actions";
import { PermitBasicDetailsDto } from "../../../permit-details/models/permit-basic-details.model";
import { PermitDetailsActions } from "../../../permit-details/states/permit-details.action";
import { PermitDetailsSelectors } from "../../../permit-details/states/permit-details.selectors";
import { PermitCompononentEnum } from "../../models/permit-component.enum.";
import { CurrentStep } from "../../models/current-step.model";
import { PermitStepActions } from "../../states/permit-step.action";
import { PermitStepSelectors } from "../../states/permit-step.selectors";

interface PermitHeaderBadge {
    type: "premium" | "flammable-atex";
    textKey: "premium" | "list.flammableAtexBadge";
}

interface PermitHeaderViewModel {
    currentComponent: PermitCompononentEnum;
    details: PermitBasicDetailsDto | undefined;
    hasWorkplace: boolean;
    permitBadge: PermitHeaderBadge | undefined;
    showBaseBadge: boolean;
    showDemoBadge: boolean;
}

@UntilDestroy()
@Component({
    templateUrl: "permit.component.html",
    styleUrls: ["permit.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild("workplaceSection", { static: false })
    public workplaceSection: ElementRef<HTMLDivElement> | undefined;

    @Select(PermitStepSelectors.currentComponent)
    public currentComponent$: Observable<PermitCompononentEnum>;

    @Select(PermitStepSelectors.currentStep)
    public currentStep$: Observable<CurrentStep>;

    @Select(PermitCreateSelectors.isDemo)
    public isDemo$: Observable<boolean>;

    @Select(PermitCreateSelectors.isPremium)
    public isPremium$: Observable<boolean>;

    @Select(PermitCreateSelectors.isFlammableAtex)
    public isFlammableAtex$: Observable<boolean>;

    @Select(PermitCreateSelectors.isBase)
    public isBase$: Observable<boolean>;

    @Select(PermitDetailsSelectors.basicDetails)
    public details$: Observable<PermitBasicDetailsDto>;

    public headerViewModel$!: Observable<PermitHeaderViewModel>;

    public permitId: number | undefined;
    private resizeObserver: ResizeObserver | undefined;
    private nextNavigationUrl: string | undefined;
    private readonly openedFromBrowserHistory = this.router.getCurrentNavigation()?.trigger === "popstate";

    public PermitPaths = PermitPaths;
    public PermitCompononent = PermitCompononentEnum;

    constructor(
        private store: Store,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private elementRef: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnDestroy(): void {
        if (!this.isNavigatingToFlammableAtexPermitFlow()) {
            this.store.dispatch(new PermitCreateActions.ResetToDefault());
        }
        this.store.dispatch(new PermitDetailsActions.Reset());

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    public ngOnInit(): void {
        this.headerViewModel$ = combineLatest([
            this.currentComponent$,
            this.isDemo$,
            this.isPremium$,
            this.isFlammableAtex$,
            this.isBase$,
            this.details$,
        ]).pipe(map(([
            currentComponent,
            isDemo,
            isPremium,
            isFlammableAtex,
            isBase,
            details,
        ]) => {
            const showDemoBadge = isDemo === true || details?.isDemo === true;
            const showPremiumBadge = isFlammableAtex !== true
                && ((isPremium === true && isDemo !== true) || (details?.isPremium === true && details?.isDemo !== true));
            let permitBadge: PermitHeaderBadge | undefined;

            if (isFlammableAtex === true) {
                permitBadge = { type: "flammable-atex", textKey: "list.flammableAtexBadge" };
            } else if (showPremiumBadge) {
                permitBadge = { type: "premium", textKey: "premium" };
            }

            return {
                currentComponent,
                details,
                hasWorkplace: currentComponent === PermitCompononentEnum.Create
                    && !!(details?.workplaceContainer || details?.workplace),
                permitBadge,
                showBaseBadge: isBase === true,
                showDemoBadge,
            };
        }));

        this.store.dispatch(new PermitStepActions.Reset());

        this.activatedRoute.params.pipe(untilDestroyed(this)).subscribe((params) => {
            const permitId = parseInt(params.permitId as string);
            if (params.permitId && !isNaN(permitId)) {
                this.permitId = permitId;
                this.store.dispatch(new PermitStepActions.GetCurrentStep(this.permitId));
                this.store.dispatch(new PermitDetailsActions.GetBasicDetails(this.permitId));
            }
        });

        this.router.events.pipe(
            untilDestroyed(this),
            filter((event): event is NavigationStart => event instanceof NavigationStart),
        ).subscribe((event) => {
            this.nextNavigationUrl = event.url;
        });

        this.currentStep$.pipe(
            untilDestroyed(this),
            filter((currentStep) =>
                this.openedFromBrowserHistory
                && this.activatedRoute.snapshot.queryParamMap.get("returnAfterStarted") === "true"
                && currentStep?.step === PermitStep.started,
            ),
            take(1),
        ).subscribe(() => {
            const returnToPermitId = Number(this.activatedRoute.snapshot.queryParamMap.get("returnToPermitId"));
            const target = !Number.isNaN(returnToPermitId) && returnToPermitId > 0
                ? [PermitPaths.Root, returnToPermitId.toString()]
                : [PermitPaths.Root];

            void this.router.navigate(target, { replaceUrl: true });
        });

        combineLatest([this.details$, this.currentComponent$]).pipe(
            untilDestroyed(this),
            debounceTime(100),
            distinctUntilChanged(),
        ).subscribe(() => {
            setTimeout(() => {
                this.updateWorkplaceHeight();
                this.setupResizeObserver();
            }, 0);
        });
    }

    public ngAfterViewInit(): void {
        this.updateWorkplaceHeight();
        this.setupResizeObserver();
    }

    private setupResizeObserver(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = undefined;
        }

        if (this.workplaceSection && window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => {
                this.updateWorkplaceHeight();
            });
            this.resizeObserver.observe(this.workplaceSection.nativeElement);
        }
    }

    private updateWorkplaceHeight(): void {
        const currentComponent = this.store.selectSnapshot(PermitStepSelectors.currentComponent);

        if (this.workplaceSection && currentComponent === PermitCompononentEnum.Create) {
            const height = this.workplaceSection.nativeElement.offsetHeight;

            this.elementRef.nativeElement.style.setProperty("--workplace-height", `${height}px`);

            this.cdr.detectChanges();
        } else if (currentComponent !== PermitCompononentEnum.Create) {
            this.elementRef.nativeElement.style.setProperty("--workplace-height", "0px");
        }
    }

    private isNavigatingToFlammableAtexPermitFlow(): boolean {
        if (!this.nextNavigationUrl) {
            return false;
        }

        return this.nextNavigationUrl.includes(PermitPaths.RequestFlammableAtexPermit)
            || this.nextNavigationUrl.includes(PermitPaths.RetrieveFlammableAtexPermit);
    }
}
